// Front-end for the Careers page (Pages/Help/Careers.cshtml). Four jobs:
//   1. Header search icon → opens the full-screen Help search overlay (identical
//      to the article Template page; the icon keeps href="/help" as a no-JS
//      fallback — initHelpSearchOverlay binds the trigger + preventDefault).
//   2. Fold / unfold each job card — both the top-right toggle and the Learn more
//      button flip the card's .collapsed state. Accordion: only one card is open at
//      a time, so opening one folds the others. Cards start folded; the .job-fold
//      regions animate open/closed and Learn more hides when open (the CSS in
//      Careers.cshtml.scss does the rest).
//   3. "Meet our team" carousel — the prev/next arrows scroll the track by one
//      card width; arrows disable at each end.
//   4. Scroll-reveal the job cards as they enter the viewport (no-ops in embedded
//      mode / under prefers-reduced-motion), matching the other pages.
import { debounce } from 'lodash-es';
import { initHelpSearchOverlay } from '../../Assets/helpSearch';
import { revealOnScroll } from '../../Assets/scrollReveal';

// Header search icon → overlay (initHelpSearchOverlay binds [data-help-search]).
initHelpSearchOverlay();

// Fold / unfold job cards (accordion — only one open at a time) from the top-right
// toggle or the Learn more button.
const cards = Array.from(document.querySelectorAll<HTMLElement>('.job-card'));

const setCollapsed = (card: HTMLElement, collapsed: boolean): void => {
    card.classList.toggle('collapsed', collapsed);
    card.querySelector<HTMLButtonElement>('.job-toggle')?.setAttribute('aria-expanded', String(!collapsed));
};

cards.forEach((card) => {
    const flip = (event: Event): void => {
        event.preventDefault();
        const willExpand = card.classList.contains('collapsed');
        // Accordion — fold every other card before opening this one.
        if (willExpand) {
            cards.forEach((other) => {
                if (other !== card) setCollapsed(other, true);
            });
        }
        setCollapsed(card, !willExpand);
    };

    card.querySelector<HTMLButtonElement>('.job-toggle')?.addEventListener('click', flip);
    card.querySelector<HTMLAnchorElement>('.job-more')?.addEventListener('click', flip);
});

// "Meet our team" carousel — arrows scroll the track by one card step; each
// disables at its end so you can't scroll past.
const rushersTrack = document.querySelector<HTMLElement>('.rushers-track');
const rushersPrev = document.querySelector<HTMLButtonElement>('.rushers-arrow.prev');
const rushersNext = document.querySelector<HTMLButtonElement>('.rushers-arrow.next');

if (rushersTrack && rushersPrev && rushersNext) {
    const track = rushersTrack;

    // One slot + the flex gap — how far each arrow press scrolls (the slot is the
    // flex item; the card inside fills it).
    const step = (): number => {
        const slot = track.querySelector<HTMLElement>('.rusher-slot');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return slot ? slot.offsetWidth + gap : track.clientWidth;
    };

    const updateArrows = (): void => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        rushersPrev.disabled = track.scrollLeft <= 1;
        rushersNext.disabled = track.scrollLeft >= maxScroll - 1;
    };

    rushersPrev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    rushersNext.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', debounce(updateArrows, 150));
    updateArrows();
}

revealOnScroll('.careers-jobs .jobs-title, .job-card');
